import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUnits } from '../lib/units';

// A bespoke dropdown rather than <select> — gives us full typographic control
// (Fraunces for the unit label, Manrope for the building line) and matches
// the editorial aesthetic. Keyboard: Enter / Space toggles, Esc closes,
// ArrowUp/Down moves focus through the list.
export default function UnitSwitcher({ className = '' }: { className?: string }) {
    const { t, i18n } = useTranslation();
    const { units, active, setActive, loading } = useUnits();
    const [open, setOpen]   = useState(false);
    const [hover, setHover] = useState(0);
    const wrapRef = useRef<HTMLDivElement>(null);
    const isAr = i18n.language === 'ar';

    // Close on outside click + Esc.
    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    if (loading && !units.length) {
        return <div className={`text-ink-muted text-sm ${className}`}>{t('common.loading')}</div>;
    }
    if (!units.length) {
        return <div className={`text-ink-muted text-sm ${className}`}>{t('units.empty')}</div>;
    }

    function chooseLabel(u: typeof units[number]): { primary: string; secondary: string } {
        return {
            primary:   u.untNm,
            secondary: isAr ? (u.bldNmA || u.bldNm || '') : u.bldNm,
        };
    }

    function selectByIndex(i: number) {
        const u = units[i];
        if (u) { setActive(u.cntID); setOpen(false); }
    }

    return (
        <div ref={wrapRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((s) => !s)}
                onKeyDown={(e) => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHover(0); }
                }}
                className="group flex items-center gap-2 sm:gap-3 px-3 py-2 -mx-3
                           rounded-[2px] hover:bg-slate-fog transition-colors w-full sm:w-auto min-w-0 sm:min-w-[200px]"
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <div className="flex flex-col items-start min-w-0 text-left">
                    <span className="eyebrow text-ink-dim leading-none mb-1">{t('units.switcherLabel')}</span>
                    {active ? (
                        <span className="flex items-baseline gap-2 min-w-0">
                            <span className="font-display text-ink-on text-lg leading-none truncate">
                                {chooseLabel(active).primary}
                            </span>
                            <span className="font-body text-xs text-ink-muted truncate">
                                {chooseLabel(active).secondary}
                            </span>
                        </span>
                    ) : (
                        <span className="text-ink-muted text-sm">{t('units.selectFirst')}</span>
                    )}
                </div>
                <Caret open={open} />
            </button>

            {open && (
                <div
                    role="listbox"
                    className="absolute z-30 top-full mt-2 start-0 w-[min(300px,calc(100vw-2rem))] max-h-[400px]
                               overflow-auto surface shadow-paper py-1.5 animate-fade-in"
                >
                    {units.map((u, i) => {
                        const { primary, secondary } = chooseLabel(u);
                        const isActive = u.cntID === active?.cntID;
                        return (
                            <button
                                key={u.cntID}
                                role="option"
                                aria-selected={isActive}
                                onClick={() => selectByIndex(i)}
                                onMouseEnter={() => setHover(i)}
                                className={`w-full text-start px-4 py-3 flex items-baseline justify-between gap-3
                                            transition-colors
                                            ${hover === i ? 'bg-slate-fog' : ''}
                                            ${isActive ? 'border-s-2 border-ember' : 'border-s-2 border-transparent'}`}
                            >
                                <div className="flex flex-col min-w-0">
                                    <span className="font-display text-ink-on text-[15px] leading-tight">
                                        {primary}
                                    </span>
                                    <span className="font-body text-xs text-ink-muted leading-tight mt-0.5">
                                        {secondary}
                                    </span>
                                </div>
                                <span className="font-body text-[10px] uppercase tracking-widest2 text-ink-dim">
                                    {u.currency || '—'}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function Caret({ open }: { open: boolean }) {
    return (
        <svg width="10" height="10" viewBox="0 0 10 10" className={`transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true">
            <path d="M1 3.5 L5 7.5 L9 3.5" stroke="#8a8780" strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
